import type { JSX, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import {
  Box, ButtonBase, CircularProgress, styled,
} from '@mui/material';
import { ChevronDownIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import { DESIGN_BORDER, TIMELINE_COLOURS } from '@/constants/PatientDashboard/patientTimeline';

const COUNT_SPINNER_SIZE = 14;

const SectionButton = styled(ButtonBase)({
  width: '100%',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  padding: '6px 8px',
  background: corePalette.grey30,
  borderRadius: '4px 4px 0 0',
  color: corePalette.offBlack100,
  '&:hover': {
    background: DESIGN_BORDER,
  },
});

const CountBadge = styled('span')({
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 8px',
  borderRadius: '4px',
  background: TIMELINE_COLOURS.neutralBorder,
  color: corePalette.offBlack100,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '130%',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
});

interface IProps {
  title: string;
  count: number;
  isOpen: boolean;
  /** True while the data this section draws from is still in flight. */
  isLoading?: boolean;
  onToggle: () => void;
  children?: ReactNode;
}

export default function Section({
  title,
  count,
  isOpen,
  onToggle,
  isLoading = false,
  children,
}: IProps): JSX.Element {
  const handleToggle = (event: ReactMouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <Box>
      <SectionButton
        onClick={handleToggle}
        disableRipple
      >
        <ChevronDownIcon
          size={16}
          strokeWidth={1.8}
          style={{
            flexShrink: 0,
            color: corePalette.grey200,
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 150ms ease',
          }}
        />
        <CustomTypography variant="bodySmall" fontWeight="medium">
          {title}
        </CustomTypography>
        {isLoading ? (
          <CircularProgress size={COUNT_SPINNER_SIZE} sx={{ color: corePalette.grey200 }} />
        ) : (
          <CountBadge>{count}</CountBadge>
        )}
      </SectionButton>
      {isOpen && children}
    </Box>
  );
}
